/**
 * Applies the organization's UI font once the application configs arrive.
 *
 * Renders nothing. It exists because the font's source of truth is split
 * across two very different timescales:
 *
 *  - The BOOT font is decided before the bundle runs, by `applyBootFont` in
 *    `App`. That is what paints the first frame.
 *  - The ORGANIZATION'S opt-out (`useRobotoFont`) is an application config,
 *    fetched after auth, so it cannot exist until well after first paint.
 *
 * So for an org that has opted out, one swap is unavoidable — nothing can
 * know the value before it is fetched — though it is a reflow of an
 * already-loaded face, not a blank.
 *
 * Two things keep that rare. The boot font is `DEFAULT_UI_FONT_ID`, which is
 * what every org gets unless it opts out, so most sessions never change font
 * at all; and `applyBootFont` replays the last resolved choice, so even an
 * opted-out org only sees the swap on a genuinely first-ever load.
 *
 * Mounted inside the redux Provider, above the routes, so it is not tied to
 * any one page's lifecycle.
 */

import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import {
  selectIsApplicationConfigsLoaded,
  selectUiFontId,
} from '@common/state/modules/app';
import { applyUiFont, rememberUiFont } from '@common/styles/fonts';
// While the folder view's dev font dropdown is enabled, a local choice
// outranks the org's, so switching fonts survives a reload. With the dropdown
// off — which is how it ships — this is always false and the config decides.
// Delete this import together with the dropdown.
import { hasDevFontOverride } from '@redact-components/CaseMedia/fontPreview';

const UiFontSync = () => {
  const isLoaded = useSelector(selectIsApplicationConfigsLoaded);
  const uiFontId = useSelector(selectUiFontId);

  useEffect(() => {
    // Before the configs land, `selectUiFontId` reports the default — which
    // is already applied. Waiting avoids a pointless write, and more
    // importantly avoids overwriting a value with the same value on every
    // store change.
    if (!isLoaded || hasDevFontOverride()) {
      return;
    }
    applyUiFont(uiFontId);
    rememberUiFont(uiFontId);
  }, [isLoaded, uiFontId]);

  return null;
};

export default UiFontSync;
